import { useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  LabelList
} from 'recharts';

const AreasMejora = () => {
  const [timeRange, setTimeRange] = useState('30D');
  const [dateRange] = useState('Jan 01, 2025 - Feb 20, 2025');

  // Datos para el radar de habilidades blandas
  const habilidadesBlandas = [
    { subject: 'Comunicación efectiva', A: 85 },
    { subject: 'Trabajo en equipo', A: 75 },
    { subject: 'Liderazgo', A: 65 },
    { subject: 'Resolución de problemas', A: 80 },
    { subject: 'Adaptabilidad', A: 70 },
    { subject: 'Gestión del tiempo', A: 60 },
    { subject: 'Pensamiento crítico', A: 75 },
    { subject: 'Inteligencia emocional', A: 85 }
  ];

  // Datos para errores comunes en CV
  const erroresCV = [
    { error: 'Falta de palabras clave relevantes', cantidad: 68 },
    { error: 'Formato inconsistente', cantidad: 55 },
    { error: 'Experiencia no relevante', cantidad: 42 },
    { error: 'Falta de logros cuantificables', cantidad: 75 },
    { error: 'Errores ortográficos', cantidad: 35 },
    { error: 'CV demasiado largo', cantidad: 48 },
    { error: 'Información de contacto incompleta', cantidad: 25 }
  ].sort((a, b) => b.cantidad - a.cantidad);

  // Datos para fallas en entrevistas
  const fallasEntrevista = [
    { falla: 'Respuestas sin estructura STAR', valor: 82 },
    { falla: 'Falta de investigación previa', valor: 65 },
    { falla: 'Lenguaje corporal inadecuado', valor: 58 },
    { falla: 'No hacer preguntas relevantes', valor: 72 },
    { falla: 'Impuntualidad', valor: 45 },
    { falla: 'Vestimenta inapropiada', valor: 38 },
    { falla: 'Falta de seguimiento', valor: 52 }
  ].sort((a, b) => b.valor - a.valor);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Áreas de Mejora por Estudiante</h1>
          <p className="mt-1 text-sm text-gray-500">
            Análisis detallado de las áreas que requieren atención y desarrollo
          </p>
          <p className="mt-1 text-sm text-gray-400">{dateRange}</p>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setTimeRange('7D')}
            className={`px-3 py-1 text-sm font-medium rounded-md ${
              timeRange === '7D'
                ? 'text-primary bg-primary/5'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            7D
          </button>
          <button
            onClick={() => setTimeRange('30D')}
            className={`px-3 py-1 text-sm font-medium rounded-md ${
              timeRange === '30D'
                ? 'text-primary bg-primary/5'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            30D
          </button>
          <button
            onClick={() => setTimeRange('90D')}
            className={`px-3 py-1 text-sm font-medium rounded-md ${
              timeRange === '90D'
                ? 'text-primary bg-primary/5'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            90D
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Radar de Habilidades Blandas */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900">Habilidades blandas (entrevistas)</h2>
          <div className="mt-6" style={{ height: '400px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart cx="50%" cy="50%" outerRadius="80%" data={habilidadesBlandas}>
                <PolarGrid gridType="polygon" />
                <PolarAngleAxis
                  dataKey="subject"
                  tick={{ fill: '#4B5563', fontSize: 12 }}
                  style={{ fontSize: '12px' }}
                />
                <PolarRadiusAxis
                  angle={30}
                  domain={[0, 100]}
                  tick={{ fill: '#4B5563' }}
                />
                <Radar
                  name="Habilidades"
                  dataKey="A"
                  stroke="#024579"
                  fill="#028bbf"
                  fillOpacity={0.6}
                />
                <Tooltip />
                <Legend />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Errores comunes en CV */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900">Errores comunes en CV</h2>
          <div className="mt-6" style={{ height: '400px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                layout="vertical"
                data={erroresCV}
                margin={{ top: 20, right: 30, left: 40, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" domain={[0, 100]} />
                <YAxis
                  dataKey="error"
                  type="category"
                  width={150}
                  tick={{ fontSize: 12 }}
                />
                <Tooltip />
                <Bar
                  dataKey="cantidad"
                  name="Frecuencia"
                  fill="#024579"
                  radius={[0, 4, 4, 0]}
                >
                  <LabelList
                    dataKey="cantidad"
                    position="right"
                    formatter={(value) => `${value}%`}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Fallas frecuentes en entrevistas */}
        <div className="bg-white shadow rounded-lg p-6 col-span-2">
          <h2 className="text-lg font-medium text-gray-900">Fallas frecuentes en entrevistas</h2>
          <div className="mt-6" style={{ height: '400px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={fallasEntrevista}
                margin={{ top: 20, right: 30, left: 20, bottom: 100 }}
                barSize={40}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="falla"
                  interval={0}
                  tick={({ x, y, payload }) => (
                    <g transform={`translate(${x},${y})`}>
                      <text
                        x={0}
                        y={0}
                        dy={16}
                        textAnchor="end"
                        fill="#666"
                        transform="rotate(-35)"
                        fontSize={12}
                      >
                        {payload.value}
                      </text>
                    </g>
                  )}
                  height={100}
                />
                <YAxis
                  domain={[0, 100]}
                  label={{ value: 'Frecuencia (%)', angle: -90, position: 'insideLeft', offset: 0 }}
                />
                <Tooltip />
                <Bar
                  dataKey="valor"
                  name="Frecuencia"
                  fill="#00bf63"
                >
                  <LabelList dataKey="valor" position="top" formatter={(value) => `${value}%`} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AreasMejora; 